import React, { useState, useRef, useEffect, useCallback } from 'react';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  sort: number;
  is_primary: boolean;
}

interface MediaCarouselProps {
  media: MediaItem[];
  className?: string;
  autoPlay?: boolean;
  showNavigation?: boolean;
  showArrows?: boolean;
  showDots?: boolean; // 新增：控制是否显示点状导航
  showLargePlayButton?: boolean;
  onVideoPlayStateChange?: (isPlaying: boolean) => void;
  fitMode?: 'cover' | 'contain';
  enableImageZoom?: boolean;
}

const MediaCarousel: React.FC<MediaCarouselProps> = ({
  media,
  className = '',
  autoPlay = false,
  showNavigation = true,
  showArrows = true,
  showDots = true, // 默认显示点状导航
  showLargePlayButton = false,
  onVideoPlayStateChange,
  fitMode = 'cover',
  enableImageZoom = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(autoPlay);
  const [overlayHidden, setOverlayHidden] = useState(false);
  const [progress, setProgress] = useState(0);
  const [autoPlayError, setAutoPlayError] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  const onVideoPlayStateChangeRef = useRef(onVideoPlayStateChange);

  // Keep ref up to date
  useEffect(() => {
    onVideoPlayStateChangeRef.current = onVideoPlayStateChange;
  }, [onVideoPlayStateChange]);

  if (!media || media.length === 0) {
    return (
      <div className={`media-carousel-placeholder ${className}`}>
        <div className="no-media-text">No Media</div>
      </div>
    );
  }

  const sortedMedia = [...media].sort((a, b) => a.sort - b.sort);
  const currentMedia = sortedMedia[currentIndex];
  const shouldShowLargeControls = showLargePlayButton || autoPlayError;
  const containerClasses = [
    'media-carousel',
    className,
    isVideoPlaying && showLargePlayButton ? 'video-playing' : '',
    fitMode === 'contain' ? 'contain-mode' : '',
    enableImageZoom ? 'zoom-enabled' : '',
    enableImageZoom && isZoomed ? 'zoomed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const mediaStyle: React.CSSProperties =
    fitMode === 'contain'
      ? {
          objectFit: 'contain',
          width: '100%',
          height: 'auto',
          maxHeight: '70vh',
          backgroundColor: 'var(--color-black)',
        }
      : {
          objectFit: 'cover',
          width: '100%',
          height: '100%',
        };

  const goToNext = () => setCurrentIndex((prev) => (prev === sortedMedia.length - 1 ? 0 : prev + 1));
  const goToPrevious = () => setCurrentIndex((prev) => (prev === 0 ? sortedMedia.length - 1 : prev - 1));
  const goToSlide = (index: number) => setCurrentIndex(index);

  const attemptAutoPlay = useCallback(async () => {
    if (!autoPlay || currentMedia.type !== 'video') {
      return;
    }

    const video = videoRef.current;
    if (!video) {
      return;
    }

    try {
      video.muted = true;
      video.setAttribute('muted', 'true');
      video.setAttribute('playsinline', 'true');
      video.setAttribute('autoplay', 'true');
      await video.play();
      setIsVideoPlaying(true);
      setAutoPlayError(false);
      // Use ref to avoid dependency issues
      if (onVideoPlayStateChangeRef.current) {
        onVideoPlayStateChangeRef.current(true);
      }
    } catch (error) {
      console.warn('Auto-play failed:', error);
      setIsVideoPlaying(false);
      setAutoPlayError(true);
      if (onVideoPlayStateChangeRef.current) {
        onVideoPlayStateChangeRef.current(false);
      }
    }
  }, [autoPlay, currentMedia.type]); // Removed onVideoPlayStateChange from deps

  useEffect(() => {
    const video = videoRef.current;

    if (currentMedia.type !== 'video') {
      setIsVideoPlaying(false);
      setAutoPlayError(false);
      setProgress(0);
      setIsZoomed(false);
      return;
    }

    if (video) {
      video.currentTime = 0;
      if (autoPlay) {
        attemptAutoPlay();
      } else {
        video.pause();
        setIsVideoPlaying(false);
        setAutoPlayError(false);
      }
    }

    setProgress(0);
  }, [currentMedia, autoPlay, attemptAutoPlay]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || currentMedia.type !== 'video') {
      return;
    }

    const handleLoadedData = () => {
      if (autoPlay) {
        attemptAutoPlay();
      }
    };

    video.addEventListener('loadeddata', handleLoadedData);
    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [autoPlay, attemptAutoPlay, currentMedia.type]);

  const handleLargePlayButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (video) {
      if (isVideoPlaying) {
        video.pause();
      } else {
        video.play().catch(console.error);
      }
    }
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
    setAutoPlayError(false);
    if (onVideoPlayStateChangeRef.current) {
      onVideoPlayStateChangeRef.current(true);
    }
    if (shouldShowLargeControls) {
      hideTimeoutRef.current = setTimeout(() => setOverlayHidden(true), 2000) as unknown as number;
    }
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
    if (onVideoPlayStateChangeRef.current) {
      onVideoPlayStateChangeRef.current(false);
    }
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setOverlayHidden(false);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video) {
      const duration = video.duration || 0;
      if (duration > 0) {
        setProgress((video.currentTime / duration) * 100);
      } else {
        setProgress(0);
      }
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (video) {
      const rect = e.currentTarget.getBoundingClientRect();
      const seekTime = ((e.clientX - rect.left) / rect.width) * video.duration;
      video.currentTime = seekTime;
    }
  };

  const toggleFullScreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (!document.fullscreenElement) {
        video.requestFullscreen().catch(err => {
        alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleMouseMove = () => {
    if (shouldShowLargeControls && isVideoPlaying) {
      setOverlayHidden(false);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = setTimeout(() => setOverlayHidden(true), 2000);
    }
  };

  const handleImageClick = () => {
    if (enableImageZoom && currentMedia.type === 'image') {
      setIsZoomed(true);
    }
  };

  const closeZoom = () => setIsZoomed(false);

  useEffect(() => {
    if (!isZoomed) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsZoomed(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isZoomed]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return (
    <div
      ref={carouselRef}
      className={containerClasses}
      onMouseMove={handleMouseMove}
    >
      <div className="carousel-main">
        {currentMedia.type === 'video' ? (
          <video
            ref={videoRef}
            src={currentMedia.url}
            className="carousel-media"
            autoPlay={autoPlay}
            muted={autoPlay} // 自动播放时静音
            loop={autoPlay}   // 自动播放时循环
            playsInline
            preload="auto"
            controls={false} // 始终使用自定义控件
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
            onTimeUpdate={handleTimeUpdate}
            poster={currentMedia.type === 'video' ? '' : currentMedia.url}
            style={mediaStyle}
          />
        ) : (
          <>
            <img
              src={currentMedia.url}
              alt=""
              className="carousel-media"
              onClick={enableImageZoom ? handleImageClick : undefined}
              role={enableImageZoom ? 'button' : undefined}
              tabIndex={enableImageZoom ? 0 : undefined}
              onKeyDown={
                enableImageZoom
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleImageClick();
                      }
                    }
                  : undefined
              }
              style={mediaStyle}
            />
            {enableImageZoom && (
              <div className="image-zoom-indicator" onClick={handleImageClick}>
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  <path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/>
                </svg>
              </div>
            )}
          </>
        )}

        {shouldShowLargeControls && currentMedia.type === 'video' && (
          <>
            <div
              className={`video-play-overlay ${overlayHidden ? 'hidden' : ''}`}
              onClick={handleLargePlayButtonClick}
            >
              {isVideoPlaying ? '⏸' : '▶'}
            </div>
            <div className={`custom-video-controls ${overlayHidden ? 'hidden' : ''}`}>
              <div className="progress-bar-container" onClick={handleSeek}>
                <div className="progress-bar" style={{ width: `${progress}%` }} />
              </div>
              <button className="fullscreen-button" onClick={toggleFullScreen}>
                <svg viewBox="0 0 24 24" width="100%" height="100%"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"></path></svg>
              </button>
            </div>
          </>
        )}
      </div>

      {showNavigation && sortedMedia.length > 1 && (
        <>
          {showArrows && (
            <>
              <button className="carousel-nav prev" onClick={goToPrevious} type="button" aria-label="Previous media">‹</button>
              <button className="carousel-nav next" onClick={goToNext} type="button" aria-label="Next media">›</button>
            </>
          )}

          {showDots && (
            <div className="carousel-dots">
              {sortedMedia.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </>
      )}

      {enableImageZoom && currentMedia.type === 'image' && isZoomed && (
        <div className="media-zoom-overlay" onClick={closeZoom} role="dialog" aria-label="Enlarged product media">
          <button className="zoom-close-btn" onClick={closeZoom} aria-label="Close enlarged view">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
          <img src={currentMedia.url} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default MediaCarousel;